import RecoverableImage from '../images/RecoverableImage';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AppUiScope from '../../app-ui/AppUiScope';
import baseline, { appCopy, appTextStyle } from '../../app-ui/baseline';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { DiscoverySection } from '../../components/content/DiscoveryContent';
import ContentTitle from '../../components/content/ContentTitle';
import HydratedPlaylistArtwork from '../../components/content/HydratedPlaylistArtwork';
import Chevron from '../../components/content/Chevron';
import { albumPath } from '../../components/content/albumPath';
import {
  RecommendationSlot,
  recommendationEvent,
  useRecommendationTracking,
} from '../discovery/RecommendationTracking';
import SponsoredExposure from '../ads/SponsoredExposure';
import { CampaignLink, SponsoredLabel } from '../ads/SponsoredContent';
import { useSponsoredPlacements } from '../ads/useSponsoredPlacements';
import SupportPromotionCard from '../sponsorship/SupportPromotionCard';
import { searchHomeContent, searchReviewExcerpt } from './homePresentation';
import './SearchHomeContent.css';
const home = baseline.search.home;
const text = baseline.contentText;
const typography = baseline.tokens.typography;

function Tracked({ tracking, section, data, className, to, label, children }) {
  const { record } = useRecommendationTracking();
  const event =
    tracking && recommendationEvent(data, 'SEARCH_HOME', section, tracking.item, tracking.position);
  return (
    <RecommendationSlot event={event} className={className}>
      <Link
        to={to}
        aria-label={label}
        onClick={() => {
          if (event)
            record([
              {
                ...event,
                eventType: 'RECOMMENDATION_CLICK',
                context: `search_home_${section === 'ALBUMS' ? 'taste' : section === 'REVIEWS' ? 'review' : 'playlist'}`,
              },
            ]);
        }}
      >
        {children}
      </Link>
    </RecommendationSlot>
  );
}
function TasteCard({ card, data }) {
  const [failed, setFailed] = useState(null);
  const media = card.imageUrl && failed !== card.imageUrl;
  const copy = (
    <div className="pp-search-taste-copy">
      {card.eyebrow && (
        <span
          className="pp-search-taste-reason"
          style={appTextStyle(home.discoveryEyebrow, text.label)}
        >
          {card.eyebrow}
        </span>
      )}
      <ContentTitle
        title={card.title}
        explicit={card.explicit}
        inlineExplicit
        style={appTextStyle(typography.albumTitle, home.discoveryCardTitle)}
      />
      <span className="pp-search-idle-meta" style={appTextStyle(text.metadata)}>
        {card.subtitle}
      </span>
    </div>
  );
  const background = card.imageUrl ? (
    <>
      <RecoverableImage
        className="pp-search-taste-image"
        src={card.imageUrl}
        alt=""
        loading="lazy"
        onFailureChange={(value) => setFailed(value ? card.imageUrl : null)}
        fallback={<span className="pp-search-taste-fallback" />}
      />
      {media && <span className="pp-search-taste-scrim" />}
    </>
  ) : (
    <span className="pp-search-taste-fallback" />
  );
  const className = `pp-search-taste-card${media ? ' pp-search-taste-media' : ''}`;
  if (card.campaign)
    return (
      <SponsoredExposure
        className={className}
        campaignId={card.campaign.id}
        placementKey="SEARCH_TASTE"
      >
        {background}
        <CampaignLink
          campaign={card.campaign}
          placementKey="SEARCH_TASTE"
          destination={card.to}
          className="pp-search-taste-ad-link"
        >
          <span>{card.title}</span>
        </CampaignLink>
        <div className="pp-search-taste-ad-copy">
          <SponsoredLabel campaign={card.campaign} placementKey="SEARCH_TASTE" />
          {copy}
        </div>
      </SponsoredExposure>
    );
  return (
    <Tracked
      className={className}
      tracking={card.tracking}
      data={data}
      section="ALBUMS"
      to={card.to}
      label={`${card.eyebrow}: ${card.title}`}
    >
      {background}
      {copy}
    </Tracked>
  );
}
export default function SearchHomeContent({ query, homeQuery, children }) {
  const { locale, t: webT } = useWebLocale();
  const t = (key, params) => appCopy(locale, key, params);
  const campaigns = useSponsoredPlacements('SEARCH_TASTE');
  const content = searchHomeContent(query.data, homeQuery.data, locale, campaigns);
  const variables = {
    '--search-idle-gap': `${home.discoveryHome.gap}px`,
    '--search-card-gap': `${home.discoveryGrid.gap}px`,
    '--search-card-height': `${home.discoveryCardMedia.minHeight}px`,
    '--search-card-radius': `${home.discoveryCard.borderRadius}px`,
    '--search-card-padding': `${home.discoveryCardCopy.padding}px`,
    '--search-card-copy-gap': `${home.discoveryCardCopy.gap}px`,
    '--search-row-gap': `${home.reviewDiscoveryRow.gap}px`,
    '--search-row-padding': `${home.reviewDiscoveryRow.paddingVertical}px`,
    '--search-row-copy-gap': `${home.reviewDiscoveryAlbum.gap}px`,
    '--search-review-height': `${home.reviewDiscoveryRow.minHeight}px`,
    '--search-playlist-height': `${home.playlistDiscoveryRow.minHeight}px`,
    '--search-quote-width': home.reviewDiscoveryQuote.width,
    '--search-review-offset': `${home.reviewDiscoveryList.marginTop}px`,
    '--search-playlist-offset': `${home.playlistDiscoveryList.marginTop}px`,
    '--search-cover': `${home.reviewDiscoveryImage.width}px`,
    '--search-cover-radius': `${home.reviewDiscoveryImage.borderRadius}px`,
  };
  return (
    <AppUiScope className="pp-search-idle" surface="lists">
      <div className="pp-search-idle-sections" style={variables}>
        <div className="pp-search-start-pair">
          {children}
          {homeQuery.isPending && <p role="status">{webT('loading')}</p>}
          {homeQuery.isError && (
            <div role="alert">
              <p>{t('search.suggestionError')}</p>
              <button className="pp-action" onClick={() => homeQuery.refetch()}>
                {t('common.retry')}
              </button>
            </div>
          )}
          {!!content.albums.length && (
            <>
              <DiscoverySection title={t('search.discoverByTaste')}>
                <div className="pp-search-taste-grid">
                  {content.albums.map((card) => (
                    <TasteCard card={card} data={query.data} key={card.key} />
                  ))}
                </div>
              </DiscoverySection>
              <SupportPromotionCard surface="SEARCH" afterSection="taste" />
            </>
          )}
        </div>
        <div className="pp-search-discovery-pair">
          {!!content.reviews.length && (
            <>
              <DiscoverySection title={t('search.discoverFromReviews')} to="/reviews" inlineMore>
                <div className="pp-search-idle-reviews">
                  {content.reviews.map((item) => (
                    <Tracked
                      key={item.reviewId}
                      tracking={item.tracking}
                      data={query.data}
                      section="REVIEWS"
                      className="pp-search-idle-review"
                      to={`${albumPath(item)}/user-reviews/${encodeURIComponent(item.reviewId)}`}
                      label={t('search.openReview', {
                        album: item.albumTitle,
                        user: item.userNickname || '',
                      })}
                    >
                      <span className="pp-search-idle-quote">
                        <svg aria-hidden="true" width="12" height="12" viewBox="0 0 14 14">
                          <path
                            d="M0 7.1C0 3.7 1.7 1.3 5 0l.8 1.8C4 2.6 3.1 3.8 2.9 5.2h2.8V10H0V7.1Zm8.2 0c0-3.4 1.7-5.8 5-7.1l.8 1.8c-1.8.8-2.7 2-2.9 3.4H14V10H8.2V7.1Z"
                            fill="currentColor"
                          />
                        </svg>
                        <span style={appTextStyle(text.preview)}>
                          {searchReviewExcerpt(item, locale)}
                        </span>
                      </span>
                      <span className="pp-search-idle-review-album">
                        {item.albumImageUrl && (
                          <RecoverableImage
                            src={item.albumImageUrl}
                            alt=""
                            loading="lazy"
                            className="pp-search-idle-cover"
                          />
                        )}
                        <span className="pp-search-idle-copy">
                          <ContentTitle
                            title={item.albumTitle}
                            explicit={item.albumContentRating === 'EXPLICIT'}
                            inlineExplicit
                          />
                          <span className="pp-search-idle-artist" style={appTextStyle(text.artist)}>
                            {item.artist?.name}
                          </span>
                        </span>
                      </span>
                    </Tracked>
                  ))}
                </div>
              </DiscoverySection>
              <SupportPromotionCard surface="SEARCH" afterSection="reviews" />
            </>
          )}
          {!!content.playlists.length && (
            <>
              <DiscoverySection title={t('search.discoverFromPlaylists')} to="/lists" inlineMore>
                <div className="pp-search-idle-playlists">
                  {content.playlists.map((item) => (
                    <Tracked
                      key={item.id}
                      tracking={item.tracking}
                      data={query.data}
                      section="PLAYLISTS"
                      className="pp-search-idle-playlist"
                      to={`/lists/${encodeURIComponent(item.slug || item.id)}`}
                      label={t('search.openPlaylist', { title: item.title })}
                    >
                      <HydratedPlaylistArtwork playlist={item} />
                      <span className="pp-search-idle-copy">
                        <span
                          className="pp-search-idle-playlist-title"
                          style={appTextStyle(typography.bodyMedium)}
                        >
                          {item.title}
                        </span>
                        <span className="pp-search-idle-meta" style={appTextStyle(text.metadata)}>
                          {item.description?.trim() ||
                            t(
                              item.type === 'ALBUM'
                                ? 'search.connectFavoriteAlbums'
                                : 'search.connectFavoriteMusic',
                            )}
                        </span>
                      </span>
                      <Chevron />
                    </Tracked>
                  ))}
                </div>
              </DiscoverySection>
              <SupportPromotionCard surface="SEARCH" afterSection="playlists" />
            </>
          )}
        </div>
      </div>
    </AppUiScope>
  );
}
